import * as zod from 'zod';

import type { BaseFeatureFlagDefinition, Section } from './featureFlags';

export type DevelopmentFeatureFlagDefinitionSchema =
  BaseFeatureFlagDefinition & {
    section?: Section;
    owner: string;
  };

// Feature flags moved from customer feature flags to development
export const DevelopmentFeatureFlagDataZod = zod.object({
  waypoints: zod.boolean().default(false),
  enableScrewdriverPickup: zod.boolean().default(false),
  enableJointLevelFirmwareUpdates: zod.boolean().default(false),
  skills: zod.boolean().default(false),
  complianceMode: zod.boolean().default(false),
  complianceModePositionCommands: zod.boolean().default(false),
  captureStep: zod.boolean().default(false),
  miniArm: zod.boolean().default(false),
  enableFirmwareAutoUpdate: zod.boolean().default(false),
  enableFirmwareMismatch: zod.boolean().default(false),
  newCNCIntegration: zod.boolean().default(false),
  ewellixLiftTLT: zod.boolean().default(false),
  weldMachineIntegration: zod.boolean().default(false),
  localAccuracyCalibration: zod.boolean().default(false),
  customDevices: zod.boolean().default(false),
  customBase: zod.boolean().default(false),
  allowCustomBaseMatchJointAngles: zod.boolean().default(false),
  backups: zod.boolean().default(false),
  systemMonitorShutdown: zod.boolean().default(false),
  tooltipAcceleration: zod.boolean().default(false),
  enableWeldTooltipAccelerationEditing: zod.boolean().default(false),
  arcStepInMove: zod.boolean().default(false),
  disableArcStepInWeld: zod.boolean().default(false),
  onRobotHexE: zod.boolean().default(false),
  antigravityToggle: zod.boolean().default(false),
  antigravityStep: zod.boolean().default(false),
  ros2Bridge: zod.boolean().default(false),
  zmqPoseMigration: zod.boolean().default(false),
  useControlSourceManager: zod.boolean().default(false),
  enableWeldBlends: zod.boolean().default(false),
  disableElastokinIK: zod.boolean().default(false),
  useExpressionsForPayload: zod.boolean().default(false),
  allowSubstepsMoveStepV2: zod.boolean().default(false),
  disableWeldWeaving: zod.boolean().default(false),
  motionPlanResampling: zod.boolean().default(false),
  avoidCacheLargeMotionPlans: zod.boolean().default(false),
  treatEncoderIssuesAsWarnings: zod.boolean().default(false),
  treatPRCSyncAndInterpolatorErrorsAsWarnings: zod.boolean().default(true),
  treatShakeDetectedAsWarning: zod.boolean().default(false),
  treatWindupLUTNotCalculatedAsWarning: zod.boolean().default(false),
  motionPlanCompression: zod.boolean().default(false),
  waypointTimingScaling: zod.boolean().default(false),
  checkpointBasedLocate: zod.boolean().default(false),
  torqueAwareMotions: zod.boolean().default(false),
  useJerkLimits: zod.boolean().default(false),
  enableOptimizerLinearPlanner: zod.boolean().default(true),
  useCppCameraBot: zod.boolean().default(false),
  teleopStep: zod.boolean().default(false),
  stopEarlyIfForce: zod.boolean().default(false),
  disableDynamicHardwareConfig: zod.boolean().default(false),
  teleopSendHeadPose: zod.boolean().default(false),
  teleopTcpOffset: zod.boolean().default(false),
  selfSignedFFEnabled: zod.boolean().default(true),
  waypointOrientationGate: zod.boolean().default(false),
  validationCentralization: zod.boolean().default(false),
  thorBetaRestrictions: zod.boolean().default(false),
  teleopRosMessages: zod.boolean().default(false),
  mountingBracket: zod.boolean().default(false),
  localCpuInference: zod.boolean().default(false),
  batchJogDirectionsValidation: zod.boolean().default(true),
  thorMotionPlanCacheTolerance: zod.boolean().default(false),
  externalControlStep: zod.boolean().default(false),
  miraiRunSkill: zod.boolean().default(false),
  secondaryBotWebsocketState: zod.boolean().default(false),
  appRedesign: zod.boolean().default(false),
  lpfPlanner: zod.boolean().default(false),
  controlBoxResourceMetrics: zod.boolean().default(false),
  externalControlBridge: zod.boolean().default(false),
  showDummyInferenceModel: zod.boolean().default(false),
  persistentVariables: zod.boolean().default(false),
  nonPlanarLocate: zod.boolean().default(false),
  disableRos2BagRecorder: zod.boolean().default(false),
  mujocoSimulator: zod.boolean().default(false),
  aiSelfServe: zod.boolean().default(false),
  useTrajectoryController: zod.boolean().default(false),
  matchRelativeToCurrentFrames: zod.boolean().default(false),
});

export type DevelopmentFeatureFlagData = zod.infer<
  typeof DevelopmentFeatureFlagDataZod
>;
export type DevelopmentFeatureFlagKey = keyof DevelopmentFeatureFlagData;

export const developmentFeatureFlagDefinitions: Record<
  DevelopmentFeatureFlagKey,
  DevelopmentFeatureFlagDefinitionSchema
> = {
  disableElastokinIK: {
    title: 'Disable Elastokin IK Solver',
    section: 'movement',
    allowSingleUser: false,
    description:
      'Disable Elastokin IK Solver for ROS2 (defaults to enabled on RO1, disabled on Thor/Spark)',
    owner: 'reyn',
  },
  waypoints: {
    title: 'Old Waypoints',
    section: 'movement',
    allowSingleUser: true,
    owner: 'henry',
  },
  matchRelativeToCurrentFrames: {
    title: 'Match read/write frames for relative-to-current moves',
    section: 'movement',
    allowSingleUser: false,
    description:
      'Relative-to-current moves read the current pose and command the target in the same TCP frame, preventing flange↔TCP frame-mismatch nosedives.',
    owner: 'alex',
  },
  arcStepInMove: {
    title: 'Arc Step in Move',
    description: 'Enable the Arc Step inside Move Arm steps (still under test)',
    section: 'movement',
    allowSingleUser: false,
    owner: 'henry',
  },
  disableArcStepInWeld: {
    title: 'Disable Arc Step in Weld',
    description: 'Disable the Arc Step inside Weld steps (defaults to enabled)',
    section: 'movement',
    allowSingleUser: false,
    owner: 'henry',
  },
  batchJogDirectionsValidation: {
    title: 'Batch jog direction validation',
    description: 'Use ROS batch service to validate jog buttons',
    section: 'movement',
    allowSingleUser: true,
    owner: 'reyn',
  },
  enableScrewdriverPickup: {
    title: 'OnRobot Screwdriver pickup',
    section: 'equipment',
    allowSingleUser: false,
    owner: 'paige',
  },
  enableJointLevelFirmwareUpdates: {
    title: 'Firmware update UI',
    section: 'infra',
    allowSingleUser: true,
    owner: 'shreyas',
  },
  skills: {
    title: 'Enable skills',
    description: 'Enable all AI features',
    section: 'AI',
    owner: 'kavan',
  },
  complianceMode: {
    title: 'Compliance mode',
    description:
      'Run compliance controller as the pose-based real-time controller',
    section: 'AI',
    owner: 'jacob',
  },
  complianceModePositionCommands: {
    title: 'Compliance Mode Position Commands',
    section: 'AI',
    allowSingleUser: false,
    description:
      'Use position commands (ROS_JOINT_TRAJECTORY) instead of effort commands (ROS_EFFORT) in compliance mode',
    owner: 'Alex',
  },
  captureStep: {
    title: 'Capture Step',
    description: 'Enable the Capture Step',
    section: 'vision',
    allowSingleUser: true,
    owner: 'raphael',
  },
  miniArm: {
    title: 'Mini Arm',
    description: 'Add mini-arm control',
    owner: 'connor',
  },
  enableFirmwareAutoUpdate: {
    title: 'Firmware auto update',
    section: 'infra',
    allowSingleUser: true,
    owner: 'shreyas',
  },
  enableFirmwareMismatch: {
    title: 'Firmware mismatch UI',
    section: 'infra',
    allowSingleUser: true,
    owner: 'shreyas',
  },
  newCNCIntegration: {
    title: 'New CNC integrations in equipment manager',
    section: 'equipment',
    allowSingleUser: true,
    owner: 'christine',
  },
  ewellixLiftTLT: {
    title: 'Ewellix Lift TLT',
    section: 'equipment',
    allowSingleUser: true,
    owner: 'christine',
  },
  weldMachineIntegration: {
    title: 'Weld machine integration',
    section: 'equipment',
    allowSingleUser: true,
    owner: 'henry',
  },
  localAccuracyCalibration: {
    title: 'Local Accuracy Calibration',
    section: 'environmentAndSpace',
    allowSingleUser: false,
    description:
      'Allows the user to calibrate the accuracy of the arm when moving to positions.',
    owner: 'shreyas',
  },
  customDevices: {
    title: 'Custom Devices',
    section: 'equipment',
    allowSingleUser: false,
    description: 'Enable custom devices',
    owner: 'christine',
  },
  customBase: {
    title: 'Custom Base',
    section: 'equipment',
    allowSingleUser: false,
    description: 'Enable custom robot base/mounting configuration',
    owner: 'jacob',
  },
  allowCustomBaseMatchJointAngles: {
    title: 'Allow Custom Base Match Joint Angles',
    section: 'equipment',
    allowSingleUser: false,
    description: 'Allow to match joint angles when a custom base is enabled',
    owner: 'jacob',
  },
  backups: {
    title: 'Backups',
    section: 'infra',
    allowSingleUser: false,
    description: 'Show backups option in robot menu',
    owner: 'shreyas',
  },
  systemMonitorShutdown: {
    title: 'System Monitor Shutdown',
    section: 'safety',
    allowSingleUser: true,
    description: 'Shut down control box when system reaches critical levels',
    owner: 'shreyas',
  },
  tooltipAcceleration: {
    title: 'Tooltip Acceleration',
    section: 'movement',
    description: 'Enable tooltip acceleration controls for safety settings',
    owner: 'henry',
  },
  enableWeldTooltipAccelerationEditing: {
    title: 'Weld Tooltip Acceleration Editing',
    section: 'movement',
    description:
      'Allow editing tooltip acceleration in weld steps. When off, acceleration is fixed at 50 mm/s².',
    owner: 'henry',
  },
  antigravityToggle: {
    title: 'Antigravity Toggle',
    section: 'movement',
    allowSingleUser: true,
    description: 'Enable non-wrist antigravity toggle',
    owner: 'paige',
  },
  antigravityStep: {
    title: 'Antigravity Step',
    section: 'movement',
    allowSingleUser: false,
    description: 'Enable Antigravity Step',
    owner: 'jacob',
  },
  onRobotHexE: {
    title: 'OnRobot Hex-E',
    section: 'equipment',
    allowSingleUser: true,
    description: 'Enable OnRobot Hex-E integration',
    owner: 'jacob',
  },
  ros2Bridge: {
    title: 'ROS2 Bridge',
    section: 'infra',
    allowSingleUser: false,
    description:
      'Enable `ros2-bridge-bot` to bridge ROS2 topics to the local network',
    owner: 'shreyas',
  },
  zmqPoseMigration: {
    title: 'ZMQ Pose Migration',
    section: 'infra',
    allowSingleUser: true,
    description: 'Enable migration of pose messaging to ZMQ',
    owner: 'jacob',
  },
  useControlSourceManager: {
    title: 'Control Source Manager',
    section: 'movement',
    allowSingleUser: false,
    description:
      'Use the control source manager to manage what pose source can control the arm.',
    owner: 'connor',
  },
  enableWeldBlends: {
    title: 'Weld Blends',
    section: 'movement',
    allowSingleUser: false,
    description: 'Enable blends for weld waypoints',
    owner: 'henry',
  },
  useExpressionsForPayload: {
    title: 'Use Expressions for Payload',
    section: 'movement',
    allowSingleUser: true,
    description: 'Enable use of expressions for payload',
    owner: 'reyn',
  },
  allowSubstepsMoveStepV2: {
    title: 'Allow certain new substeps in move step v2',
    section: 'movement',
    allowSingleUser: true,
    description: 'Enable allow set io and set variable move step v2',
    owner: 'reyn',
  },
  disableWeldWeaving: {
    title: 'Disable Weld Weaving',
    section: 'movement',
    allowSingleUser: true,
    description:
      'Disable the weaving option in the weld step wizard (defaults to enabled on Core, disabled on Thor)',
    owner: 'santiago',
  },
  motionPlanResampling: {
    title: 'Motion Plan Resampling',
    section: 'movement',
    allowSingleUser: true,
    description: 'Enable resampling of weaving motion plans',
    owner: 'santiago',
  },
  avoidCacheLargeMotionPlans: {
    title: 'Avoid Cache Large Motion Plans',
    section: 'movement',
    allowSingleUser: true,
    description: 'Enable avoiding cache of large motion plans',
    owner: 'santiago',
  },
  treatEncoderIssuesAsWarnings: {
    title: 'Treat Encoder Issues as Warnings',
    section: 'safety',
    allowSingleUser: true,
    description: 'Treat encoder issues as warnings instead of errors',
    owner: 'paige',
  },
  treatPRCSyncAndInterpolatorErrorsAsWarnings: {
    title: 'Treat PRC Sync and Interpolator Error as Warnings',
    section: 'safety',
    allowSingleUser: true,
    description:
      'Treat PRC sync and interpolator error as warnings instead of errors',
    owner: 'paige / ryan',
  },
  treatShakeDetectedAsWarning: {
    title: 'Treat Shake Detected as Warning',
    section: 'safety',
    allowSingleUser: false,
    description:
      'Treat ShakeDetected ResultType as a warning instead of an error',
    owner: 'ryan',
  },
  treatWindupLUTNotCalculatedAsWarning: {
    title: 'Treat Windup LUT Not Calculated as Warning',
    section: 'safety',
    allowSingleUser: false,
    description:
      'Treat missing windup calibration (WindupLUTNotCalculated) as a warning instead of a violation',
    owner: 'aneek',
  },
  motionPlanCompression: {
    title: 'Motion Plan Compression',
    section: 'movement',
    allowSingleUser: false,
    description: 'Enable compression for motion plan cache',
    owner: 'freddy',
  },
  waypointTimingScaling: {
    title: 'Different speed per waypoints',
    section: 'movement',
    allowSingleUser: false,
    description: 'Enable per-waypoint timing scaling control in Move Arm',
    owner: 'lovro',
  },
  checkpointBasedLocate: {
    title: 'Checkpoint-based Locate',
    section: 'AI',
    allowSingleUser: true,
    description:
      'Enable checkpoint-based locate functionality for improved object detection and positioning',
    owner: 'kavan',
  },
  torqueAwareMotions: {
    title: 'Torque-aware motions',
    section: 'movement',
    allowSingleUser: false,
    description: 'Enable motion planning using torque limits',
    owner: 'jacob',
  },
  useJerkLimits: {
    title: 'Use Jerk Limits',
    section: 'movement',
    allowSingleUser: false,
    description: 'Enable use of jerk limits for motion planning',
    owner: 'lovro',
  },
  enableOptimizerLinearPlanner: {
    title: 'Optimizer Linear Planner',
    section: 'movement',
    allowSingleUser: false,
    description:
      'Include OptimizerLinearPlanner in the multi-planner for TYPE_LINEAR connections',
    owner: 'reyn',
  },
  useCppCameraBot: {
    title: 'Use C++ Camera Bot',
    section: 'AI',
    allowSingleUser: false,
    description: 'Switch to use C++ camera bot (leaves legacy for wrist cam)',
    owner: 'cnr',
  },
  teleopStep: {
    title: 'Teleop Step',
    section: 'AI',
    allowSingleUser: false,
    description: 'Enable teleop step for routines',
    owner: 'derek',
  },
  stopEarlyIfForce: {
    title: 'Stop Early If Force',
    section: 'movement',
    allowSingleUser: true,
    description: 'Enable stop early if force for waypoints',
    owner: 'rox',
  },
  disableDynamicHardwareConfig: {
    title: 'Disable Dynamic Hardware Config',
    section: 'equipment',
    allowSingleUser: false,
    description:
      'Disable loading dynamic hardware config from firmware (defaults to enabled)',
    owner: 'aneek',
  },
  teleopSendHeadPose: {
    title: 'Teleop Head Pose UDP',
    section: 'AI',
    allowSingleUser: false,
    description: 'Enable POC head tracking via udp',
    owner: 'cnr',
  },
  teleopTcpOffset: {
    title: 'Teleop TCP Offset',
    section: 'AI',
    allowSingleUser: false,
    description:
      'Teleop using the gripper/TCP offset instead of the wrist position.',
    owner: 'michael',
  },
  selfSignedFFEnabled: {
    title: 'Self-Signed HTTPS Certificates',
    section: 'infra',
    allowSingleUser: true,
    owner: 'derek',
    description:
      'Enable self-signed local HTTPS certificates and botctl certificates command',
  },
  waypointOrientationGate: {
    title: 'Waypoint Orientation Gate',
    description:
      'Require orientation within thresholds (in addition to distance) to mark waypoint reached',
    section: 'movement',
    allowSingleUser: true,
    owner: 'derekhe',
  },
  validationCentralization: {
    title: 'Validation Centralization',
    description:
      'Enforce same validation across all sources of routine running',
    section: 'movement',
    allowSingleUser: false,
    owner: 'derekhe',
  },
  thorBetaRestrictions: {
    title: 'Thor Beta Restrictions',
    section: 'safety',
    allowSingleUser: false,
    description:
      'When enabled, restricts default and max velocities/accelerations for Thor arm joints to beta-safe values',
    owner: 'shreyas',
  },
  teleopRosMessages: {
    title: 'Teleop ROS Messages',
    section: 'AI',
    allowSingleUser: false,
    description: 'Enable ROS messages for teleop',
    owner: 'michael',
  },
  mountingBracket: {
    title: 'Mounting Bracket',
    section: 'equipment',
    allowSingleUser: false,
    description:
      'Enable mounting bracket end effector equipment for mounting adapters',
    owner: 'jacob',
  },
  localCpuInference: {
    title: 'Local CPU Inference',
    section: 'AI',
    allowSingleUser: false,
    description:
      'Enable running AI inference locally on CPU without external GPU Task Runner',
    owner: 'derek',
  },
  thorMotionPlanCacheTolerance: {
    title: 'Increase Thor Motion Plan Cache Tolerance',
    section: 'movement',
    allowSingleUser: false,
    description:
      'Use a wider start joint tolerance for ROS motion plan cache on Thor to reduce replanning from joint settling drift',
    owner: 'livanov93',
  },
  externalControlStep: {
    title: 'External Control Step',
    section: 'movement',
    allowSingleUser: false,
    description: 'Enable external control step',
    owner: 'jacob',
  },
  miraiRunSkill: {
    title: 'MIRAI Run Skill',
    section: 'AI',
    allowSingleUser: false,
    description: 'Enable MIRAI Controller equipment and MIRAI Run Skill step',
    owner: 'jacob',
  },
  secondaryBotWebsocketState: {
    title: 'Secondary Bot WebSocket State',
    section: 'equipment',
    allowSingleUser: false,
    description:
      'Enable subscribing to secondary bot RoutineRunnerState via WebSocket in StandardBotsRO1 integration',
    owner: 'michael',
  },
  appRedesign: {
    title: 'App Redesign',
    section: 'newUI',
    allowSingleUser: false,
    description: 'Enable the redesigned app UI',
    owner: 'christine',
  },
  lpfPlanner: {
    title: 'LPF Planner',
    section: 'movement',
    description:
      'Use low-pass filter planner instead of spline planner for streaming interfaces',
    owner: 'Alex',
  },
  disableRos2BagRecorder: {
    title: 'Disable ROS2 Bag Recorder',
    section: 'movement',
    allowSingleUser: false,
    description:
      'When enabled, forces the ROS2 bag recorder off on control boxes (where it would otherwise run by default). No effect in dev/VM environments.',
    owner: 'reyn',
  },
  controlBoxResourceMetrics: {
    title: 'Control Box Resource Metrics',
    section: 'infra',
    allowSingleUser: false,
    description:
      'Enable Prometheus + cAdvisor monitoring stack for per-container resource metrics',
    owner: 'connor',
  },
  externalControlBridge: {
    title: 'External Control Bridge',
    section: 'infra',
    allowSingleUser: false,
    description:
      'Enable UDP real-time streaming bridge in external-control-bot',
    owner: 'jacob',
  },
  persistentVariables: {
    title: 'Persistent Variables',
    section: 'environmentAndSpace',
    description:
      'Allow environment variables to retain their last runtime value across routine executions',
    owner: 'roxanne',
  },
  nonPlanarLocate: {
    title: 'Non-Planar Locate',
    section: 'vision',
    allowSingleUser: false,
    description: 'Enable non-planar plane estimation in the Locate step.',
    owner: 'derek',
  },
  mujocoSimulator: {
    title: 'MuJoCo Simulator',
    section: 'AI',
    allowSingleUser: false,
    description:
      'Enable MuJoCo simulator features: virtual camera discovery, scene object API for codeblocks',
    owner: 'lee',
  },
  aiSelfServe: {
    title: 'AI Self Serve',
    section: 'AI',
    allowSingleUser: false,
    description: 'Enable AI self-serve features',
    owner: 'zaina',
  },
  showDummyInferenceModel: {
    title: 'Show dummy inference model',
    section: 'AI',
    allowSingleUser: true,
    description:
      'Show a dummy circle model option in the model selector for camera-free sim testing',
    owner: 'kavan',
  },
  useTrajectoryController: {
    title: 'Trajectory Controller',
    section: 'movement',
    description:
      'Enable the 1kHz trajectory controller in arm-control-bot for move primitives (servoJ, servoL, speedJ, speedL, executeTrajectory)',
    owner: 'freddy',
  },
};

type GroupedKeys = { section: Section; keys: DevelopmentFeatureFlagKey[] }[];

export const underDevelopmentFeatureFlags: GroupedKeys = (() => {
  const grouped: GroupedKeys = [];

  for (const keyStr of Object.keys(developmentFeatureFlagDefinitions)) {
    const key = keyStr as DevelopmentFeatureFlagKey;
    let { section } = developmentFeatureFlagDefinitions[key];

    if (!section) {
      section = 'other';
    }

    const group = grouped.find((g) => g.section === section);

    if (group) {
      group.keys.push(key);
    } else {
      grouped.push({ section, keys: [key] });
    }
  }

  // sort by section
  return grouped.sort((a, b) => {
    if (a.section === 'other') {
      return 1;
    }

    if (b.section === 'other') {
      return -1;
    }

    return a.section.localeCompare(b.section);
  });
})();
